import { useState, useEffect } from 'react';
import { getSettingForAdmin } from '../../../services/settingService';
import GeneralSettings from './settings/GeneralSettings';
import SEOSettings from './settings/SEOSettings';
import SocialSettings from './settings/SocialSettings';
import EmailSettings from './settings/EmailSettings';
import FooterSettings from './settings/FooterSettings';
import BackupSettings from './settings/BackupSettings';
import SecuritySettings from './settings/SecuritySettings';

interface SettingsManagementProps {
  activeTabFromUrl?: string;
  setActiveTabInUrl?: (tab: string) => void;
}

const SettingsManagement = ({ activeTabFromUrl = 'settings', setActiveTabInUrl }: SettingsManagementProps) => {
  // Lấy tab từ URL hoặc mặc định là 'general'
  const getCurrentTab = () => {
    if (activeTabFromUrl.startsWith('settings-')) {
      return activeTabFromUrl.replace('settings-', '');
    }
    return 'general';
  };

  const [activeTab, setActiveTab] = useState(getCurrentTab());
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Update activeTab khi URL thay đổi
  useEffect(() => {
    setActiveTab(getCurrentTab());
  }, [activeTabFromUrl]);

  // Hàm để thay đổi tab và update URL
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (setActiveTabInUrl) {
      setActiveTabInUrl(`settings-${tab}`);
    }
  };

  // Fetch settings khi component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getSettingForAdmin();
        if (response.success && response.data) {
          setSettings(response.data);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Không thể tải cài đặt');
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const tabs = [
    { id: 'general', label: 'Cài đặt chung', icon: 'ri-settings-line' },
    { id: 'seo', label: 'SEO & Meta', icon: 'ri-search-line' },
    { id: 'social', label: 'Mạng xã hội', icon: 'ri-share-line' },
    { id: 'email', label: 'Email & SMTP', icon: 'ri-mail-line' },
    { id: 'footer', label: 'Footer', icon: 'ri-file-list-line' },
    { id: 'backup', label: 'Sao lưu', icon: 'ri-database-line' },
    { id: 'security', label: 'Bảo mật', icon: 'ri-shield-keyhole-line' },
  ];

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2A4B7C]"></div>
          <p className="mt-4 text-gray-600">Đang tải cài đặt...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 text-sm text-red-600 hover:text-red-800 underline"
            >
              Tải lại trang
            </button>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'general':
        return <GeneralSettings settings={settings} onUpdate={setSettings} />;
      case 'seo':
        return <SEOSettings settings={settings} onUpdate={setSettings} />;
      case 'social':
        return <SocialSettings settings={settings} onUpdate={setSettings} />;
      case 'email':
        return <EmailSettings settings={settings} onUpdate={setSettings} />;
      case 'footer':
        return <FooterSettings settings={settings} onUpdate={setSettings} />;
      case 'backup':
        return <BackupSettings />;
      case 'security':
        return <SecuritySettings />;
      default:
        return <GeneralSettings settings={settings} onUpdate={setSettings} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Cài đặt hệ thống</h2>
        <p className="text-gray-600">Quản lý cấu hình và thiết lập website</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#2A4B7C] text-white'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon} text-lg`}></i>
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsManagement;
